import { BodyText, Strong } from '@utilitywarehouse/hearth-react';

import { DISCOUNT_IDS } from '@constants/apiConstants';
import {
  Discount,
  EnergyPaymentMethod,
  FuelBreakdown,
} from '@generated/gql/graphql';
import { formatCurrency, formatCurrencyInPence } from '@utils/format';
import { isAmountCredit } from '@utils/price';

type BreakdownSectionProps = { quote: FuelBreakdown; isDualFuel: boolean };

const findDiscount = (discounts: Discount[]) =>
  discounts.find(
    ({ id }) =>
      id === DISCOUNT_IDS.OFGEM_PRICE_CAP_DISCOUNT_DUAL_FUEL_GAS ||
      id === DISCOUNT_IDS.OFGEM_PRICE_CAP_DISCOUNT_DUAL_FUEL_ELECTRIC
  );

const BreakdownSection = ({ isDualFuel, quote }: BreakdownSectionProps) => {
  const dualFuelDiscount = findDiscount(quote.discounts);
  const hasPrepaymentMeter =
    quote.paymentMethod === EnergyPaymentMethod.Prepayment;
  const annualDiscountValue =
    dualFuelDiscount && isAmountCredit(dualFuelDiscount.credit)
      ? Number(dualFuelDiscount.credit.amount) * 12
      : 0;
  const hasBiRate = quote.unitRate2 > 0;

  return (
    <>
      <li>
        <BodyText>
          Tariff: <Strong>{quote.name}</Strong>
        </BodyText>
      </li>
      <li>
        <BodyText>
          Annual Estimate: <Strong>{formatCurrency(quote.annualCost)}</Strong>
        </BodyText>
      </li>
      {!hasPrepaymentMeter && (
        <li>
          <BodyText>
            Monthly Direct Debit:{' '}
            <Strong>{formatCurrency(quote.monthlyCost)}</Strong>
          </BodyText>
        </li>
      )}
      <li>
        <BodyText>
          Unit rate
          {hasBiRate && <span> (day)</span>}:&nbsp;
          <Strong>
            {formatCurrencyInPence({ value: quote.unitRate1 })} per kWh
          </Strong>
        </BodyText>
      </li>
      {hasBiRate && (
        <li>
          <BodyText>
            Unit rate (night):{' '}
            <Strong>
              {formatCurrencyInPence({ value: quote.unitRate2 })} per kWh
            </Strong>
          </BodyText>
        </li>
      )}
      <li>
        <BodyText>
          Daily standing charge:{' '}
          <Strong>
            {formatCurrencyInPence({ value: quote.standingCharge })}
          </Strong>
        </BodyText>
      </li>
      <li>
        <BodyText>
          Exit fee:{' '}
          <Strong>
            {formatCurrency({ value: quote.exitFees, exponent: 2 })}
          </Strong>
        </BodyText>
      </li>
      {dualFuelDiscount && isDualFuel && (
        <li>
          <BodyText>
            Discounts: Dual fuel discount{' '}
            <Strong>{formatCurrency({ value: annualDiscountValue })}</Strong>
          </BodyText>
        </li>
      )}
    </>
  );
};

export { BreakdownSection };
